import React, { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Flex,
  List,
  MenuProps,
  Modal,
  Typography,
} from "antd";
import { BsFilterLeft } from "react-icons/bs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setCurrentChat } from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { formatSingleChat } from "../../../utils";
import CustomLoader from "../../../components/common/UI/Loader";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import ContactCard from "../../../components/Chat/ContactCard";
import NewGroup from "../NewGroup";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import SearchInput from "../../../components/common/UI/SearchInput";
import NewBroadcast from "../NewBroadcast";
import "./index.scss";

const { Text } = Typography;
const FilterIcon = BsFilterLeft as unknown as React.FC;

interface NewChatSectionProps {
  handleBackToChats: () => void;
}

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
  block: string | null;
}

const NewChatSection: React.FC<NewChatSectionProps> = ({
  handleBackToChats,
}) => {
  const dispatch = useAppDispatch();
  const { chats } = useSelector((state: RootState) => state.chat);
  const { contacts: contactList } = useSelector(
    (state: RootState) => state.contacts
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [phoneNumbersLoading, setPhoneNumbersLoading] =
    useState<boolean>(false);
  const [currentScreen, setCurrentScreen] = useState("new-chat");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [efoneContactsList, setEfoneContactsList] = useState<Contact[] | null>(
    null
  );
  const [nonEfoneContactsList, setNonEfoneContactsList] = useState<
    Contact[] | null
  >(null);
  const [showeFoneList, setShoweFoneList] = useState<boolean>(true);
  const [showNoneFoneList, setShowNoneFoneList] = useState<boolean>(true);
  const [phoneNumbersModalVisible, setPhoneNumbersModalVisible] =
    useState<boolean>(false);
  const [phoneNumbersList, setPhoneNumbersList] = useState<any>(null);
  const [selectedContactInfo, setSelectedContactInfo] =
    useState<Contact | null>(null);

  const items: MenuProps["items"] = [
    {
      key: "all",
      label: "All",
    },
    {
      key: "efone",
      label: "Efone",
    },
    {
      key: "non-efone",
      label: "Non Efone",
    },
  ];

  const handleChatsFilterChange = (key: any) => {
    if (key === "efone") {
      setShoweFoneList(true);
      setShowNoneFoneList(false);
    } else if (key === "non-efone") {
      setShowNoneFoneList(true);
      setShoweFoneList(false);
    } else {
      setShoweFoneList(true);
      setShowNoneFoneList(true);
    }
  };

  const filterFn = (contact: Contact) => {
    const search = searchQuery.toLowerCase();
    return (
      contact.fullName.toLowerCase().includes(search) ||
      contact.phoneNumber.includes(search)
    );
  };

  const filteredEfoneContacts = efoneContactsList?.filter(filterFn);
  const filteredNonEfoneContacts = nonEfoneContactsList?.filter(filterFn);

  const handleCreateDummyChat = (phoneNumberDetails: any) => {
    const newChat = {
      _id: selectedContactInfo?._id,
      isGroup: false,
      groupMembers: [],
      lastMessage: "",
      iseFone: selectedContactInfo?.iseFone,
      mediaType: null,
      filePath: null,
      firstId: null,
      secondId: null,
      firstUserName: null,
      secondUserName: null,
      name: selectedContactInfo?.fullName,
      firstUserPhoneNumber: phoneNumberDetails?.phoneNumber,
      secondUserPhoneNumber: selectedContactInfo?.phoneNumber,
      lastUpdate: null,
      lastMsgId: null,
      groupSetting: {
        addOthersMembers: true,
        editGroup: true,
        sendMessage: true,
      },
      pastMembers: [],
      lastUpdateToSort: null,
      sourceNumber: selectedContactInfo?.phoneNumber,
      block: null,
      sourceUserName: null,
      isPin: null,
      unreadCount: 0,
      isMuted: null,
      isArchived: null,
      isNewChat: true,
      savedName:
        selectedContactInfo?.fullName || selectedContactInfo?.phoneNumber,
    };

    const formattedChat = formatSingleChat(newChat);
    dispatch(setCurrentChat(formattedChat));
  };

  const handleContactClick = (contact: Contact) => {
    if (!contact.iseFone) {
      toast.error("You cannot text a non-efone number");
      return;
    }
  
    const existingChat = chats.find((convo: any) => {
      return convo.sourceNumber === contact.phoneNumber;
    });

    if (existingChat) {
      dispatch(setCurrentChat(existingChat));
    } else {
      setSelectedContactInfo(contact);
      setPhoneNumbersModalVisible(true);
    }
  };

  const getPhoneNumbersList = async () => {
    const URL = ENDPOINTS.GET_PHONE_NUMBERS;

    setPhoneNumbersLoading(true);
    try {
      const response = await apiInstance.get(URL);
      const data = response?.data?.data;
      setPhoneNumbersList(data);
    } catch (err: any) {
      toast.error(err.message || "Couldn't get Phone Numbers");
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  useEffect(() => {
    if (contactList) {
      const efoneContacts: any = contactList.filter(
        (contact: any) => contact.iseFone
      );
      const nonEfoneContacs: any = contactList.filter(
        (contact: any) => !contact.iseFone
      );
      setEfoneContactsList(efoneContacts);
      setNonEfoneContactsList(nonEfoneContacs);
      setLoading(false);
    }
  }, [contactList]);

  useEffect(() => {
    if (phoneNumbersModalVisible) {
      getPhoneNumbersList();
    }
  }, [phoneNumbersModalVisible]);

  return (
    <>
      {currentScreen === "new-chat" && (
        <div className="new-chats-container">
          <ChatSidebarHeader
            title="New Chat"
            handleBackClick={handleBackToChats}
          />

          <div className="primary-section">
            <div className="input-container">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by name or phone number..."
              />
              <Dropdown
                trigger={["click"]}
                menu={{
                  items,
                  onClick: (e) => handleChatsFilterChange(e.key),
                }}
              >
                <Button type="link" className="filter-btn">
                  <FilterIcon />
                </Button>
              </Dropdown>
            </div>

            <Flex vertical>
              <Button
                onClick={() => setCurrentScreen("new-group")}
                className="action-btn"
                type="text"
              >
                <Flex align="center" gap={16}>
                  <div className="action-btn-icon">
                    <img src="/assets/icons/add-user.svg" alt="" />
                  </div>
                  <Text className="action-btn-title">New Group</Text>
                </Flex>
              </Button>
              <Button
                onClick={() => setCurrentScreen("new-broadcast")}
                className="action-btn"
                type="text"
              >
                <Flex align="center" gap={16}>
                  <div className="action-btn-icon">
                    <img src="/assets/icons/add-user.svg" alt="" />
                  </div>
                  <Text className="action-btn-title">New Broadcast</Text>
                </Flex>
              </Button>
            </Flex>
          </div>

          <div className="efone-contacts-container">
            <div className="efone-contacts-list">
              <List
                className="contacts-wrapper-list"
                loading={{
                  spinning: loading,
                  indicator: (
                    <Flex
                      className="w-full h-full"
                      justify="center"
                      align="center"
                    >
                      <CustomLoader />
                    </Flex>
                  ),
                }}
              >
                {showeFoneList && (
                  <>
                    <div className="banner">eFone Contacts</div>
                    <List
                      dataSource={filteredEfoneContacts || []}
                      renderItem={(contact: Contact) => (
                        <ContactCard
                          onClick={() => handleContactClick(contact)}
                          key={contact._id}
                          userName={contact.fullName}
                          phoneNumber={contact.phoneNumber}
                          isEfone={contact.iseFone}
                        />
                      )}
                    />
                  </>
                )}

                {showNoneFoneList && (
                  <>
                    <div className="banner">Invite to eFone</div>

                    <List
                      dataSource={filteredNonEfoneContacts || []}
                      renderItem={(contact: Contact) => (
                        <ContactCard
                          onClick={() => handleContactClick(contact)}
                          key={contact._id}
                          userName={contact.fullName}
                          phoneNumber={contact.phoneNumber}
                          isEfone={false}
                        />
                      )}
                    />
                  </>
                )}
              </List>
            </div>
          </div>

          <Modal
            centered
            className="custom-modal numbers-list-modal"
            open={phoneNumbersModalVisible}
            onCancel={() => setPhoneNumbersModalVisible(false)}
            title="Select Phone Number"
            footer={null}
          >
            <List
              className="numbers-list"
              dataSource={phoneNumbersList || []}
              loading={{
                spinning: phoneNumbersLoading,
                indicator: <CustomLoader />,
              }}
              renderItem={(item: any) => (
                <List.Item
                  key={item?._id}
                  className="number"
                  onClick={() => {
                    handleCreateDummyChat(item);
                    setPhoneNumbersModalVisible(false);
                  }}
                >
                  <Text className="number">{item?.phoneNumber}</Text>
                </List.Item>
              )}
            />
          </Modal>
        </div>
      )}

      {currentScreen === "new-group" && (
        <NewGroup handleBackClick={() => setCurrentScreen("new-chat")} />
      )}

      {currentScreen === "new-broadcast" && (
        <NewBroadcast handleBackClick={() => setCurrentScreen("new-chat")} />
      )}
    </>
  );
};

export default NewChatSection;
